import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import type { Product as DisplayProduct } from '../../types';
import { API_URL_PRODUCTS, BASE_URL } from '../../constants';
import ProductCard from './ProductCard';
import { featuredProducts } from './data/mockData';

type PageResponse<T> = {
  content?: T[];
  totalPages?: number;
  message?: string;
};

type ApiProduct = {
  id: number;
  sku?: string | null;
  name: string;
  description?: string | null;
  price?: number | null;
  image?: string | null;
  photoUrl?: string | null;
  store?: StoreSummary | null;
  storeId?: number | null;
  store_id?: number | null;
  stores?: StoreSummary[];
  productVariationCombinations?: ProductVariationCombination[];
  [key: string]: any;
};

type StoreSummary = {
  id?: number;
  name?: string;
  description?: string;
};

type ProductVariationCombination = {
  id: number;
  price?: number | null;
  available?: number | null;
};

type ProductVariation = {
  id: number;
  variation?: {
    name?: string;
  };
};

type ProductVariationValue = {
  id: number;
  value?: string;
  name?: string;
};

type ProductPhotoSet = {
  id: number;
  active?: boolean;
  priority?: number;
};

type ProductPhoto = {
  id: number;
  url?: string;
  primary?: boolean;
  position?: number;
};

interface MainProductsProps {
  searchTerm: string;
  onClearSearch: () => void;
}

const PRODUCT_VARIATIONS_URL = `${BASE_URL}/product-variations`;
const PRODUCT_VARIATION_VALUES_URL = `${BASE_URL}/product-variation-values`;
const PRODUCT_VARIATION_COMBINATIONS_URL = `${BASE_URL}/product-variation-combinations`;
const PRODUCT_PHOTO_SETS_URL = `${BASE_URL}/product-variation-photo-sets`;
const PRODUCT_PHOTOS_URL = `${BASE_URL}/product-variation-photo-set-photos`;

const fallbackImages = featuredProducts.map((product) => product.image);

const getPageItems = <T,>(data: PageResponse<T> | T[] | any): T[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  return [];
};

const normalizeText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const getPriceRange = (product: ApiProduct, combinations: ProductVariationCombination[]) => {
  const prices = combinations
    .map((combination) => Number(combination.price))
    .filter((price) => Number.isFinite(price) && price > 0);

  if (prices.length) {
    return {
      price: Math.min(...prices),
      priceMax: Math.max(...prices),
    };
  }

  const basePrice = Number(product.price || 0);
  return {
    price: basePrice,
    priceMax: basePrice,
  };
};

const getProductStore = (product: ApiProduct) => {
  const embeddedStore = product.store || product.stores?.[0] || null;
  const storeId = embeddedStore?.id ?? product.storeId ?? product.store_id ?? null;
  const storeName = embeddedStore?.name || embeddedStore?.description || (storeId ? `Local ${storeId}` : undefined);

  return {
    storeId,
    storeName,
  };
};

const fetchCombinations = async (productId: number) => {
  const response = await axios.get<PageResponse<ProductVariationCombination>>(PRODUCT_VARIATION_COMBINATIONS_URL, {
    params: { productId, page: 0, size: 100 },
  });
  return getPageItems<ProductVariationCombination>(response.data);
};

const fetchSizes = async (productId: number) => {
  const variationsResponse = await axios.get<PageResponse<ProductVariation>>(PRODUCT_VARIATIONS_URL, {
    params: { productId, page: 0, size: 50 },
  });
  const variations = getPageItems<ProductVariation>(variationsResponse.data);
  const sizeVariation = variations.find((variation) => {
    const name = normalizeText(variation.variation?.name || '');
    return name.includes('talla') || name.includes('size') || name.includes('tamano');
  });

  const targetVariations = sizeVariation ? [sizeVariation] : variations.slice(0, 1);
  const values = await Promise.all(
    targetVariations.map(async (variation) => {
      const valuesResponse = await axios.get<PageResponse<ProductVariationValue>>(PRODUCT_VARIATION_VALUES_URL, {
        params: { productVariationId: variation.id, page: 0, size: 20 },
      });
      return getPageItems<ProductVariationValue>(valuesResponse.data);
    })
  );

  return values
    .flat()
    .map((value) => value.value || value.name || '')
    .filter(Boolean)
    .slice(0, 4);
};

const fetchProductImage = async (productId: number) => {
  const setsResponse = await axios.get<PageResponse<ProductPhotoSet>>(PRODUCT_PHOTO_SETS_URL, {
    params: { productId, page: 0, size: 20 },
  });
  const photoSet = getPageItems<ProductPhotoSet>(setsResponse.data)
    .filter((set) => set.active !== false)
    .sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0) || b.id - a.id)[0];

  if (!photoSet) return null;

  const photosResponse = await axios.get<PageResponse<ProductPhoto>>(PRODUCT_PHOTOS_URL, {
    params: { photoSetId: photoSet.id, page: 0, size: 20 },
  });
  const photos = getPageItems<ProductPhoto>(photosResponse.data).sort(
    (a, b) => Number(a.position || 0) - Number(b.position || 0) || a.id - b.id
  );

  return photos.find((photo) => photo.primary)?.url || photos[0]?.url || null;
};

const MainProducts = ({ searchTerm, onClearSearch }: MainProductsProps) => {
  const [products, setProducts] = useState<DisplayProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizedSearch = useMemo(() => normalizeText(searchTerm), [searchTerm]);

  useEffect(() => {
    if (!normalizedSearch) {
      setProducts([]);
      setError(null);
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get<PageResponse<ApiProduct>>(API_URL_PRODUCTS, {
          params: { keyword: searchTerm, page: 0, size: 48 },
          signal: controller.signal,
        });
        const apiProducts = getPageItems<ApiProduct>(response.data).filter((product) =>
          normalizeText(product.name || '').includes(normalizedSearch)
        );

        const mappedProducts = await Promise.all(
          apiProducts.map(async (product, index) => {
            const [combinations, sizes, photoUrl] = await Promise.all([
              fetchCombinations(product.id).catch(() => product.productVariationCombinations || []),
              fetchSizes(product.id).catch(() => []),
              fetchProductImage(product.id).catch(() => product.photoUrl || product.image || null),
            ]);
            const priceRange = getPriceRange(product, combinations);
            const store = getProductStore(product);

            return {
              id: product.id,
              sku: product.sku || undefined,
              name: product.name,
              price: priceRange.price,
              priceMax: priceRange.priceMax,
              image: photoUrl || fallbackImages[index % fallbackImages.length],
              category: 'Producto',
              storeId: store.storeId,
              storeName: store.storeName,
              sizes,
              requiresSelection: combinations.length > 0 || sizes.length > 0,
            };
          })
        );

        setProducts(mappedProducts);
      } catch (requestError: any) {
        if (axios.isCancel(requestError) || requestError?.name === 'CanceledError') return;
        setProducts([]);
        setError('No se pudieron cargar los productos.');
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [normalizedSearch, searchTerm]);

  return (
    <main className="section container main-products-view">
      <div className="main-products-heading">
        <div>
          <span>Resultados de busqueda</span>
          <h2>Productos para "{searchTerm.trim()}"</h2>
          <p>
            Mostrando productos cuyo nombre coincide total o parcialmente con tu busqueda.
          </p>
        </div>
        <button type="button" className="secondary-button" onClick={onClearSearch}>
          Limpiar busqueda
        </button>
      </div>

      {loading ? (
        <div className="main-products-state">Buscando productos...</div>
      ) : error ? (
        <div className="main-products-state main-products-state--error">{error}</div>
      ) : products.length ? (
        <div className="product-grid">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      ) : (
        <div className="main-products-state">
          No encontramos productos con ese nombre.
        </div>
      )}
    </main>
  );
};

export default MainProducts;
